import React from 'react';
import {
  AccountPersonaDisclosureSchema,
  type AccountPersonaDisclosure,
} from '@confluent/shared/lib/account-persona/disclosure';
import { DisclosureBadge } from '../ui/DisclosureBadge.js';
import { MARKDOWN_OVERRIDES } from '../ui/markdown-overrides.js';
import { SafeMarkdown } from '../ui/SafeMarkdown.js';
import { Badge, DataTable } from '../ui';
import { buttonStyles } from '../tailgrids/core/button.js';
import { safeHref } from '../../lib/safe-url.js';
import type { WorkOutputPart } from '../../generated/api/types.gen.js';

/** What a part needs to be drawn: a generated `WorkOutputPart`, or any value carrying these fields. */
export type RenderableOutput = Pick<WorkOutputPart, 'kind' | 'title' | 'payload' | 'payloadUrl'>;

/** A part's payload as the key/value record every renderer reads (a part without one renders as empty). */
function payloadOf(output: RenderableOutput): Record<string, unknown> {
  const payload = output.payload;
  return typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {};
}

/** The account persona's disclosure on a `report`/`table` part's payload, if it carries a valid one. */
export function partDisclosure(output: RenderableOutput): AccountPersonaDisclosure | null {
  if (output.kind !== 'report' && output.kind !== 'table') return null;
  const parsed = AccountPersonaDisclosureSchema.safeParse(payloadOf(output).disclosure);
  return parsed.success ? parsed.data : null;
}

export const OutputRenderer: React.FC<{ output: RenderableOutput }> = ({ output }) => {
  const disclosure = partDisclosure(output);
  if (!disclosure) return <OutputBody output={output} />;
  return (
    <div>
      <div className="flex items-center gap-2 px-1 pb-1">
        <DisclosureBadge disclosure={disclosure} />
        <span className="truncate text-[11px] text-muted">{disclosure.text}</span>
      </div>
      <OutputBody output={output} />
    </div>
  );
};

const OutputBody: React.FC<{ output: RenderableOutput }> = ({ output }) => {
  switch (output.kind) {
    case 'summary':
      return <SummaryRenderer payload={payloadOf(output)} name={output.title} />;
    case 'table':
      return <TableRenderer payload={payloadOf(output)} name={output.title} />;
    case 'chart':
      return <ChartRenderer payload={payloadOf(output)} name={output.title} />;
    case 'signal':
      return <SignalRenderer payload={payloadOf(output)} name={output.title} />;
    case 'metric':
      return <MetricRenderer payload={payloadOf(output)} name={output.title} />;
    case 'file':
      return (
        <FileRenderer payload={payloadOf(output)} payloadUrl={output.payloadUrl ?? undefined} name={output.title} />
      );
    case 'report':
      return <ReportRenderer payload={payloadOf(output)} name={output.title} />;
    default:
      return <JsonRenderer payload={payloadOf(output)} name={output.title} />;
  }
};

// Summary — simple text card
const SummaryRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => (
  <div className="bg-surface rounded-lg border border p-4">
    <h4 className="text-sm font-semibold text-heading mb-2">{name}</h4>
    <p className="text-sm text-body">{String(payload.text ?? '')}</p>
  </div>
);

// Table — headers + rows
const TableRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => {
  const columns =
    (payload.columns as Array<{ key: string; label?: string }> | undefined) ??
    (payload.headers as string[] | undefined)?.map((header) => ({ key: header, label: header })) ??
    [];
  const rows = (payload.rows as Record<string, unknown>[]) ?? [];
  const totalRows = payload.totalRows as number | undefined;

  return (
    <div>
      <DataTable
        framed
        aria-label={name}
        columns={columns.map((column) => ({
          key: column.key,
          header: column.label ?? column.key,
          render: (row: Record<string, unknown>) => String(row[column.key] ?? ''),
        }))}
        rows={rows}
        empty={<p className="px-3 py-4 text-sm text-muted">No rows yet.</p>}
        rowKey={(_row, index) => index}
      />
      {totalRows !== undefined && totalRows > rows.length && (
        <p className="mt-1 px-1 text-xs text-faint">
          Showing {rows.length} of {totalRows}
        </p>
      )}
    </div>
  );
};

// Chart — placeholder (full chart lib integration is a follow-up)
const ChartRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => {
  const chartType = (payload.chartType as string) ?? 'bar';
  const series = (payload.series as Array<{ name: string; data: Array<{ x: string; y: number }> }>) ?? [];

  return (
    <div className="bg-surface rounded-lg border border p-4">
      <h4 className="text-sm font-semibold text-heading mb-2">{name}</h4>
      <div className="text-xs text-faint mb-2">Chart type: {chartType}</div>
      {/* Simple bar visualization — replace with recharts/lightweight-charts later */}
      {series.map((s, si) => (
        <div key={si} className="space-y-1">
          <span className="text-[10px] text-muted">{s.name}</span>
          {s.data.slice(0, 15).map((d, di) => (
            <div key={di} className="flex items-center gap-2">
              <span className="text-[10px] text-muted w-20 truncate text-right">{d.x}</span>
              <div className="flex-1 h-4 bg-surface-alt rounded-sm overflow-hidden">
                <div
                  className="h-full bg-accent rounded-sm"
                  style={{ width: `${Math.min((d.y / Math.max(...s.data.map((p) => p.y), 1)) * 100, 100)}%` }}
                />
              </div>
              <span className="text-[10px] text-muted w-12">{typeof d.y === 'number' ? d.y.toFixed(1) : d.y}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// Signal — buy/sell card
const SignalRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload }) => {
  const action = payload.action as string;
  const ticker = payload.ticker as string;
  const confidence = payload.confidence as number;
  const isBuy = action?.toLowerCase().includes('buy');

  return (
    <div
      className={`rounded-lg border p-3 ${isBuy ? 'border-success bg-success-muted/30' : 'border-danger bg-danger-muted/30'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={isBuy ? 'success' : 'danger'}>{action?.toUpperCase()}</Badge>
          <span className="font-semibold text-sm text-heading">{ticker}</span>
        </div>
        <span className="text-xs text-muted">
          {confidence !== undefined ? `${(confidence * 100).toFixed(0)}% confidence` : ''}
        </span>
      </div>
    </div>
  );
};

// Metric — value card
const MetricRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => (
  <div className="bg-surface rounded-lg border border p-3">
    <span className="text-[10px] text-faint uppercase">{name}</span>
    <div className="text-lg font-bold text-heading">
      {typeof payload.value === 'number' ? payload.value.toFixed(4) : String(payload.value)}
    </div>
    {payload.unit ? <span className="text-xs text-muted">{String(payload.unit)}</span> : null}
  </div>
);

// File — download link
const FileRenderer: React.FC<{ payload: Record<string, unknown>; payloadUrl?: string; name: string }> = ({
  payload,
  payloadUrl,
  name,
}) => (
  <div className="bg-surface rounded-lg border border p-3 flex items-center justify-between">
    <div>
      <h4 className="text-sm font-semibold text-heading">{name}</h4>
      <span className="text-xs text-faint">
        {String(payload.mimeType ?? 'application/json')} — {((payload.originalSizeBytes as number) / 1024).toFixed(1)}{' '}
        KB
      </span>
    </div>
    {payloadUrl && (
      <a
        href={safeHref(payloadUrl) ?? undefined}
        className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
        download
      >
        Download
      </a>
    )}
  </div>
);

// Report — markdown sections
// Report — its sections; a report that has only its markdown `content` (the canonical report payload is
// {format, title, content, sections}; a desk report carries no sections) renders that markdown.
const ReportRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => {
  const sections = (payload.sections as Array<{ heading: string; content: string }>) ?? [];
  const content = typeof payload.content === 'string' ? payload.content : '';
  return (
    <div className="bg-surface rounded-lg border border p-4 space-y-3">
      <h4 className="text-sm font-semibold text-heading">{name || (payload.title as string)}</h4>
      {sections.length === 0 && content.trim().length > 0 && (
        <div className="text-sm text-body">
          <SafeMarkdown forceBlock overrides={MARKDOWN_OVERRIDES}>
            {content}
          </SafeMarkdown>
        </div>
      )}
      {sections.map((s, i) => (
        <div key={i}>
          <h5 className="text-xs font-bold text-body mb-1">{s.heading}</h5>
          <pre className="text-xs text-muted whitespace-pre-wrap">{s.content}</pre>
        </div>
      ))}
    </div>
  );
};

// Fallback — raw JSON
const JsonRenderer: React.FC<{ payload: Record<string, unknown>; name: string }> = ({ payload, name }) => (
  <div className="bg-surface rounded-lg border border p-4">
    <h4 className="text-sm font-semibold text-heading mb-2">{name}</h4>
    <pre className="text-xs text-muted whitespace-pre-wrap overflow-auto max-h-48">
      {JSON.stringify(payload, null, 2)}
    </pre>
  </div>
);
